/**
 * components/client-detail/ClientDetailHeader.tsx
 *
 * Floating card header for the client detail page.
 * Layout — white rounded card:
 *   Row 1: avatar (lg) + kebab (right)
 *   Row 2: primary name
 *   Row 3: contact person line for companies
 *   Meta rows — the clickable zone is limited to [icon + value + Copy icon].
 *     The Copy icon appears inline immediately after the value on hover.
 *     The row background does NOT span the full card width.
 *     All copyable fields use the same Inter font (no font-mono).
 *   Order: Email → Phone → SIREN/company reg (Building2 icon) → "Client depuis le …"
 *
 * Created Apr 2026.
 * Updated Apr 2026 — copy icon adjacent to value, removed ExternalLink,
 *   Building2 for SIREN, aerated spacing, no blue accent line.
 * Updated Apr 2026 — click zone limited to inline content (not full width),
 *   SIREN uses same font as other fields (no font-mono).
 */

import { useState } from 'react';
import { MoreHorizontal, GitMerge, Mail, Phone, Calendar, Copy, Check, Building2 } from 'lucide-react';
import type { Database } from '../../types/database';
import ClientAvatar from '../ui/ClientAvatar';
import { getClientDisplayName, isCompany } from '../../utils/clientDisplay';
import { formatDate } from '../../utils/dateUtils';
import { useToast } from '../../contexts/ToastContext';
import DropdownMenu from '../ui/DropdownMenu';
import type { DropdownItem } from '../ui/DropdownMenu';

type Client = Database['public']['Tables']['clients']['Row'];

interface CopyableMetaRowProps {
  icon: React.ReactNode;
  value: string;
  display?: React.ReactNode;
}

function CopyableMetaRow({ icon, value, display }: CopyableMetaRowProps) {
  const [copied, setCopied] = useState(false);
  const { showToast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      showToast('Copié dans le presse-papier', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[handleCopy] clipboard write failed:', err);
      showToast('Échec de la copie', 'error');
    }
  };

  return (
    <div className="flex items-center gap-2.5 py-1">
      {/* Static icon — outside the clickable zone */}
      <span className="text-neutral-400 flex-shrink-0 w-4">{icon}</span>

      {/* Clickable zone: only wraps value + copy icon */}
      <button
        type="button"
        onClick={handleCopy}
        title="Cliquer pour copier"
        className="group/copy inline-flex items-center gap-1.5 px-1.5 py-0.5 -mx-1.5 rounded-md hover:bg-neutral-100 transition-colors"
      >
        <span className="text-sm text-neutral-700">
          {display ?? value}
        </span>
        <span className="opacity-0 group-hover/copy:opacity-100 transition-opacity flex-shrink-0">
          {copied
            ? <Check className="w-3.5 h-3.5 text-success-500" />
            : <Copy className="w-3.5 h-3.5 text-neutral-400" />
          }
        </span>
      </button>
    </div>
  );
}

interface StaticMetaRowProps {
  icon: React.ReactNode;
  children: React.ReactNode;
}

function StaticMetaRow({ icon, children }: StaticMetaRowProps) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span className="text-neutral-400 flex-shrink-0 w-4">{icon}</span>
      <span className="text-sm text-neutral-500">{children}</span>
    </div>
  );
}

interface ClientDetailHeaderProps {
  client: Client;
  onMergeClick: () => void;
}

export default function ClientDetailHeader({ client, onMergeClick }: ClientDetailHeaderProps) {
  const displayName = getClientDisplayName(client);
  const company = isCompany(client);
  const contactPerson = company
    ? [client.first_name, client.last_name].filter(Boolean).join(' ')
    : null;

  const menuItems: DropdownItem[] = [
    {
      label: 'Fusionner avec un doublon',
      icon: <GitMerge className="w-4 h-4" />,
      onClick: onMergeClick,
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-soft p-6">
      {/* Row 1: avatar + kebab */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <ClientAvatar client={client} size="lg" />
        <DropdownMenu
          trigger={
            <button
              title="Actions"
              className="p-2 rounded-xl text-neutral-400 border border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 hover:text-neutral-600 transition-all focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:outline-none"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          }
          items={menuItems}
          align="right"
        />
      </div>

      {/* Row 2: primary name */}
      <h1 className="text-2xl font-bold tracking-tight text-neutral-900 leading-snug mb-0.5">
        {displayName || 'Sans nom'}
      </h1>

      {/* Row 3: contact person for companies */}
      {company && contactPerson && (
        <p className="text-sm text-neutral-500 mb-4">{contactPerson}</p>
      )}

      {/* Meta lines: Email → Phone → SIREN → Date */}
      <div className="flex flex-col mt-4">
        {client.email && (
          <CopyableMetaRow
            icon={<Mail className="w-3.5 h-3.5" />}
            value={client.email}
          />
        )}
        {client.phone && (
          <CopyableMetaRow
            icon={<Phone className="w-3.5 h-3.5" />}
            value={client.phone}
          />
        )}
        {company && client.company_registration_number && (
          <CopyableMetaRow
            icon={<Building2 className="w-3.5 h-3.5" />}
            value={client.company_registration_number}
          />
        )}
        <StaticMetaRow icon={<Calendar className="w-3.5 h-3.5" />}>
          Client depuis le {formatDate(client.created_at)}
        </StaticMetaRow>
      </div>
    </div>
  );
}
